import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { useFetcher, useRevalidator } from '@remix-run/react';
import { clsx } from 'clsx';
import { Button, Spinner, ThemeProvider, Tooltip as FlowbiteTooltip, Alert } from 'flowbite-react';
import { Fragment, useCallback, useEffect, useMemo, useState, type PropsWithChildren, type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { useLocale } from 'remix-i18next/react';
import { Icon } from '#common/components/Icon.tsx';
import { IconButton } from '#common/components/IconButton.tsx';
import LanguagePicker from '#common/components/LanguagePicker.tsx';
import { MonochromeCrispyVoiceLogoWithText } from '#common/components/MonochromeCrispyVoiceLogoWithText.tsx';
import { ScrollableRow } from '#common/components/ScrollableRow.tsx';
import { Tooltip } from '#common/components/Tooltip.tsx';
import { renderDataAttributes, type PropsWithDataAttributes } from '#common/components/types.ts';
import { VideoPlayer } from '#common/components/VideoPlayer.tsx';
import { useActionUrlWithOrigin } from '#common/hooks/useActionUrlWithOrigin.ts';
import { useUrlWithQueryParams } from '#common/hooks/useUrlWithQueryParams.ts';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type TranslateFunc } from '#common/i18n/types.ts';
import { getMediumExamples, getMediumTitle } from '#common/medium-display.ts';
import { minimalTakeSchema, renderHiddenFieldsFromDiscriminatedUnion } from '#common/models/tts-models';
import { crispyvoiceTheme } from '#common/theme.ts';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { useAffiliateRef } from '#common/utils/tracking/affiliate-ref-context.tsx';
import { AudioProvider, useAudio } from '#speaker-shop/components/AudioContext.tsx';
import { Checkbox } from '#speaker-shop/components/Checkbox.tsx';
import { CurrencySwitcher } from '#speaker-shop/components/CurrencySwitcher.tsx';
import { Feature } from '#speaker-shop/components/Feature.tsx';
import { RecordingProvider, useRecordingContext } from '#speaker-shop/components/RecordingContext.tsx';
import { Tile } from '#speaker-shop/components/Tile.tsx';
import { useVoicePageRootData } from '#speaker-shop/hooks/useVoicePageRootData';
import { useVoicePath } from '#speaker-shop/hooks/useVoicePath';
import { useVoiceSlug } from '#speaker-shop/hooks/useVoiceSlug';
import { calculatePriceForRecording } from '#speaker-shop/models/price-calculation.ts';
import { type VoiceData } from '#speaker-shop/models/speaker-voice-data.server.ts';
import { hasLoadedAudio, isRestoredTake } from '#speaker-shop/utils/recordingStorage.ts';
import { trackCheckoutTotalSum, trackMediumSelection } from '#speaker-shop/utils/tracking.ts';
import { Arrow } from '../Arrow';
import { AudioGeneration } from '../AudioGeneration';
import { ContactFormDialog } from '../ContactFormDialog';
import { CouponEntry, type CouponInfo } from '../CouponEntry';
import { useMediumSelectionContext } from '../MediumSelectionContext';
import { PlayButton } from '../PlayButton';
import { type RecordingRow } from '../Recording';
import { StarRating } from '../StarRating';
import { type TakeRowModel } from '../TakeRow';
import { type LayoutProps, type ResolvedAvailableUse } from './types';

export function ShopLayout2(opts: LayoutProps) {
    const { selectedMediums, setSelectedMediums } = useMediumSelectionContext();
    const [ showContactForm, setShowContactForm ] = useState(false);
    const [ canOrder, setCanOrder ] = useState(false);
    const { t } = useEditableTranslation('hero');
    const {
        portraitImageSrc,
        voiceNameSvg,
        speakerNameSvg,
        portraitVideoHlsSrc,
        portraitVideoHlsAssetPath,
        audioSampleEnHlsSrc,
        audioSampleDeHlsSrc,
        locale,
    } = useVoicePageRootData();
    const audioSampleHlsSrc = locale === 'de' ? audioSampleDeHlsSrc : audioSampleEnHlsSrc;
    const initiatePurchaseUrl = useActionUrlWithOrigin('/api/initiate-purchase');
    const translatePath = useTranslatedPath(useLocale());
    const voicePath = useVoicePath();
    const voiceSlug = useVoiceSlug();
    const affiliateRef = useAffiliateRef();
    const availableUses = useMemo(() => [ ...opts.availableUses, {
        id: 'api',
        title: t('medium-selection:api.title'),
        subTitle: t('medium-selection:api.subTitle'),
        icon: 'ant-design--api-filled'
    } ], [ opts.availableUses, t ]);
    const initiatePurchaseFetcher = useFetcher();
    const formId = 'initiatePurchaseForm';

    const onMediumSelected = (selectedMedium: string) => {
        if (selectedMedium !== 'api') {
            setSelectedMediums(prev => {
                const selected = prev.indexOf(selectedMedium) !== -1
                    ? prev.filter(x => x !== selectedMedium)
                    : [ ...prev, selectedMedium ];
                trackMediumSelection(selected, opts.availableUses);
                return selected;
            });
        } else {
            setShowContactForm(true);
        }
    };

    const revalidator = useRevalidator();
    const refreshData = useCallback(() => {
        revalidator.revalidate();
    }, [ revalidator ]);

    const rating = useMemo(() => {
        if (opts.testimonials.length === 0) {
            return 0;
        }
        return opts.testimonials.reduce((acc, curr) => acc + curr.rating, 0) / opts.testimonials.length;
    }, [ opts.testimonials ]);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const testimonialsUrl = useUrlWithQueryParams(translatePath(voicePath('/testimonials')), [ 'variant' ]);

    useEffect(() => {
        const onPageShow = () => setIsSubmitting(false);
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const onSubmit = () => {
        setIsSubmitting(true);
    };

    useEffect(() => {
        if (initiatePurchaseFetcher.state === 'idle') {
            setIsSubmitting(false);
        }
    }, [ initiatePurchaseFetcher.state ]);

    return (
        <ThemeProvider theme={crispyvoiceTheme}>
            <AudioProvider>
                <div className='w-full bg-radial from-crispybluedark-600 to-crispybluedark-700 flex flex-col grow p-8 2xl:p-0'>
                    <div className='flex flex-col 2xl:flex-row grow'>
                        <div className='flex flex-row  2xl:items-start'>
                            <div className='w-full 2xl:w-100 relative flex flex-col 2xl:items-end shrink-0'>
                                <div className='grid grid-cols-1 grid-rows-1 mb-6 2xl:mb-0 items-center w-full 2xl:mt-10 px-3'>
                                    <MonochromeCrispyVoiceLogoWithText className='2xl:hidden text-white w-40 md:w-50 col-start-1 row-start-1 transition-[width] duration-300' />
                                    <div className='text-white 2xl:mr-10 col-start-1 row-start-1 justify-self-end 2xl:justify-self-end flex items-center gap-3'>
                                        <LanguagePicker baseUrl={'/'} />
                                    </div>
                                </div>
                                {rating > 0 && (
                                    <a
                                        href={testimonialsUrl}
                                        className='flex flex-row items-center mb-2 mx-auto 2xl:hidden'
                                    >
                                        <StarRating rating={rating} size='small' />
                                    </a>
                                )}

                                <div className='2xl:mt-10 w-full 2xl:mb-8'>
                                    <Portrait
                                        speakerNameSvg={speakerNameSvg}
                                        voiceNameSvg={voiceNameSvg}
                                        imageSrc={portraitImageSrc}
                                        videoHlsSrc={portraitVideoHlsSrc}
                                        videoHlsAssetPath={portraitVideoHlsAssetPath}
                                        audioSampleHlsSrc={audioSampleHlsSrc}
                                        showVideoIfAvailable={true}
                                    />
                                    <div className='2xl:hidden text-yellow-300 text-center'>{t('hero:title')}</div>
                                </div>
                            </div>
                        </div>

                        <div className='text-white 2xl:text-black 2xl:bg-white grow 2xl:rounded-bl-4xl relative 2xl:p-10 pt-0 2xl:pt-24 2xl:min-w-0'>
                            <div className='max-w-5xl hidden 2xl:block'>
                                {opts.testimonials?.length > 0 && (
                                    <a href={testimonialsUrl} className='flex flex-row items-center gap-2 mb-4'>
                                        <span>{rating.toFixed(1)}</span>
                                        <StarRating rating={rating} />
                                        <span>-</span> <span>{opts.testimonials.length} {t('hero:reviews')}</span>
                                    </a>
                                )}
                                <h1 className='text-4xl font-extrabold text-slate-900 sm:text-5xl'>{t('title')}</h1>
                                <p className='mt-6 text-3xl text-slate-600'>{t('subTitle')}</p>
                            </div>
                            <div className='relative'>
                                <div className='space-y-4 2xl:space-y-0 mt-10'>
                                    <div className='text-3xl 2xl:font-bold w-full flex flex-wrap items-center gap-y-2'>
                                        <div className='flex self-start'>
                                            <div className='self-stretch items-center flex'>
                                                <div className='flex items-center justify-center w-8 h-8 rounded-full border-1 mr-4 shrink-0'>
                                                    1
                                                </div>
                                            </div>
                                            {t('medium-selection:header')}
                                        </div>
                                        <div className='ml-auto'>
                                            <CurrencySwitcher
                                                currency={opts.currency}
                                                availableCurrencies={opts.availableCurrencies}
                                                className='text-base font-semibold'
                                            />
                                        </div>
                                        <Arrow className='ml-6 hidden 2xl:inline rotate-270 h-16 w-16 -mt-2' />
                                    </div>
                                    <MediumSelection
                                        selectedMediums={selectedMediums}
                                        onSelectedMediumChange={onMediumSelected}
                                        availableMediums={availableUses}
                                        locale={locale}
                                    />
                                </div>
                                <div className='space-y-4 2xl:space-y-0'>
                                    <div className='text-3xl 2xl:font-bold w-full flex'>
                                        <div className='self-stretch items-center flex'>
                                            <div className='flex items-center justify-center w-8 h-8 rounded-full border-1 mr-4 shrink-0'>
                                                2
                                            </div>
                                        </div>
                                        {t('audio-generation:header')}
                                        <Arrow className='2xl:ml-64 inline h-16 w-16 -mt-8 ml-auto' />
                                    </div>
                                    <RecordingProvider>
                                        <div className='2xl:rounded-2xl 2xl:bg-radial 2xl:from-crispybluedark-600 2xl:to-crispybluedark-700 2xl:p-5 2xl:mt-6'>
                                            <div className='flex flex-col 2xl:grid 2xl:grid-cols-[1fr_auto] 2xl:grid-rows-[auto_auto] 2xl:gap-4 mb-4'>
                                                <div className='h-full pb-4 2xl:pb-0'>
                                                    <AudioGenerationContainer
                                                        hasConsent={opts.hasConsent}
                                                        isLoggedIn={opts.isLoggedIn}
                                                        anonymousId={opts.anonymousId}
                                                        limits={opts.limits}
                                                        defaultAudioControls={opts.defaultAudioControls}
                                                        requestDataRefresh={refreshData}
                                                    />
                                                </div>
                                                <div className='block lg:flex lg:grid-cols-2 lg:gap-4 2xl:block 2xl:col-start-2'>
                                                    <MediumSizeRecordings />
                                                    <initiatePurchaseFetcher.Form
                                                        method='POST'
                                                        action={initiatePurchaseUrl}
                                                        id={formId}
                                                        onSubmit={onSubmit}
                                                        className='flex flex-col gap-4 md:min-w-80 2xl:max-w-80 3xl:max-w-120 2xl:h-full'
                                                    >
                                                        {voiceSlug && (
                                                            <input type='hidden' name='voiceSlug' value={voiceSlug} />
                                                        )}
                                                        {affiliateRef && (
                                                            <input type='hidden' name='ref' value={affiliateRef} />
                                                        )}
                                                        <OrderCard
                                                            allowedMediums={opts.availableUses.filter(x =>
                                                                selectedMediums.indexOf(x.id) !== -1
                                                            )}
                                                            onCanOrderChange={setCanOrder}
                                                            currency={opts.currency}
                                                        >
                                                            <Button
                                                                type='submit'
                                                                disabled={!canOrder || isSubmitting}
                                                                color='yellow'
                                                                className='2xl:col-start-2 2xl:row-start-2 h-16 text-2xl hidden lg:flex flex-row justify-center items-center gap-4 text-black cursor-pointer rounded-lg mt-4'
                                                                form={formId}
                                                            >
                                                                {isSubmitting && <Spinner size='md' className='mr-2' />}
                                                                <Icon icon='fa6-solid--cart-shopping'></Icon>
                                                                {t('checkout:order-now')}
                                                            </Button>
                                                        </OrderCard>
                                                        <Button
                                                            type='submit'
                                                            disabled={!canOrder || isSubmitting}
                                                            color='yellow'
                                                            className='w-full h-16 text-2xl lg:hidden flex flex-row justify-center items-center gap-4 text-black cursor-pointer rounded-lg p-4'
                                                            form={formId}
                                                        >
                                                            {isSubmitting && <Spinner size='md' className='mr-2' />}
                                                            <Icon icon='fa6-solid--cart-shopping'></Icon>
                                                            {t('checkout:order-now')}
                                                        </Button>
                                                    </initiatePurchaseFetcher.Form>
                                                </div>
                                            </div>
                                        </div>
                                    </RecordingProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <ContactFormDialog
                    topic={'API'}
                    description={
                        <div>
                            <p>{t('common:api-contact-form-description')}</p>
                            <p>{t('common:contact-form-instruction')}</p>
                        </div>
                    }
                    open={showContactForm}
                    onClose={() => setShowContactForm(false)}
                />
            </AudioProvider>
        </ThemeProvider>
    );
}

interface PortraitProps {
    speakerNameSvg?: string | null;
    voiceNameSvg?: string | null;
    imageSrc: string;
    videoHlsSrc?: string | null;
    videoHlsAssetPath?: string | null;
    audioSampleHlsSrc?: string | null;
    showVideoIfAvailable?: boolean;
}

export function Portrait(
    { imageSrc, speakerNameSvg, voiceNameSvg, videoHlsSrc, videoHlsAssetPath, audioSampleHlsSrc, showVideoIfAvailable }:
        PortraitProps,
) {
    const { t } = useEditableTranslation('hero');
    const { isPlayingAudio, playAudio, stopAudio } = useAudio();
    const hasAudioSample = !!audioSampleHlsSrc;

    return (
        <div className='relative z-10 mx-auto flex rounded-xl bg-slate-600 shadow-xl
                flex-col items-center justify-between bg-gradient-to-b from-slate-600 to-gray-900
                2xl:overflow-hidden
                2xl:px-6 2xl:pt-6
                2xl:w-80
                gap-y-4
                pb-4
                p-4
                mb-6 2xl:mb-0
                '>
            <div className='flex flex-col items-center gap-y-2 2xl:gap-y-4 w-full'>
                <div className='w-full justify-center hidden 2xl:flex'>
                    <img
                        src='/images/CrispyVoice_Logo.png'
                        alt='CrispyVoice Logo'
                        className='h-12 2xl:h-16 w-auto object-contain'
                    />
                </div>
                {speakerNameSvg && voiceNameSvg
                    ? (
                        <div className='text-white text-center w-40 2xl:w-full shrink-0'>
                            <div className='h-10 2xl:h-16' dangerouslySetInnerHTML={{ __html: speakerNameSvg }} />
                            <div
                                className='h-6 2xl:h-10 mt-1 opacity-70'
                                dangerouslySetInnerHTML={{ __html: voiceNameSvg }}
                            />
                        </div>
                    )
                    : (
                        <div
                            className='text-white text-center w-40 2xl:w-full h-12 2xl:h-20
                               font-bold shrink-0'
                            dangerouslySetInnerHTML={{ __html: (speakerNameSvg || voiceNameSvg)! }}
                        />
                    )}
            </div>
            {showVideoIfAvailable && videoHlsSrc
                ? (
                    <VideoPlayer
                        hlsSrc={videoHlsSrc}
                        hlsAssetPath={videoHlsAssetPath}
                        posterSrc={imageSrc}
                        autoPlay={true}
                        className='w-30 lg:w-50 h-30 lg:h-50 2xl:w-full flex justify-center object-contain mb-6 2xl:mb-0 transition-[width] duration-300 rounded-lg'
                    />
                )
                : (
                    <img
                        className='w-30 lg:w-50 h-30 lg:h-50 2xl:w-full flex justify-center object-contain rounded-full 2xl:rounded-none border-3 border-white/50 2xl:border-none mb-6 2xl:mb-0 transition-[width] duration-300'
                        style={{
                            maskImage: 'radial-gradient(circle at center, white 80%, transparent 100%)',
                            maskSize: 'cover'
                        }}
                        src={imageSrc}
                        alt='Profile Picture'
                    />
                )}
            {hasAudioSample && (
                <div className='hidden 2xl:flex flex-col items-center gap-y-2 2xl:gap-y-4'>
                    <div className='text-white font-bold'>{t('listen-to-my-voice')}</div>
                    <IconButton
                        icon={isPlayingAudio ? 'fa6-solid--circle-stop' : 'fa6-solid--circle-play'}
                        onClick={isPlayingAudio ? stopAudio : () => playAudio(audioSampleHlsSrc!)}
                        className='text-yellow-300 cursor-pointer w-12 h-12'
                    />
                </div>
            )}
            {hasAudioSample && (
                <Button
                    color='yellow'
                    className='rounded-lg absolute bottom-0 transform translate-y-1/2 2xl:hidden'
                    onClick={isPlayingAudio ? stopAudio : () => playAudio(audioSampleHlsSrc!)}
                >
                    <Icon icon={isPlayingAudio ? 'fa6-solid--stop' : 'fa6-solid--play'} className='mr-2' />
                    {t('listen-to-my-voice')}
                </Button>
            )}
        </div>
    );
}

interface MediumSelectionProps {
    selectedMediums: string[];
    onSelectedMediumChange: (selectedMedium: string) => void;
    availableMediums: (ResolvedAvailableUse | {
        id: string;
        icon: string;
        title: ReactNode;
        subTitle: ReactNode;
        children?: ReactNode;
    })[];
    locale?: string;
}

function MediumSelection(
    { selectedMediums, availableMediums, onSelectedMediumChange, locale = 'en' }: MediumSelectionProps,
) {
    const { t } = useEditableTranslation('medium-selection');

    const sizeClassNames = 'w-60 h-36 2xl:w-64 2xl:h-36';

    return (
        <div className='pb-8'>
            <ScrollableRow chevronColor='text-white 2xl:text-crispybluedark-600' className='hidden lg:block'>
                <div className='gap-x-6 lg:flex flex-row px-6 pb-12 pt-6 hidden w-fit'>
                    {availableMediums.map(x => {
                        let icon: string;
                        let title: ReactNode;
                        let subTitle: ReactNode;
                        let children: ReactNode | null;
                        let info: ReactNode | undefined;
                        const isSelected = selectedMediums.indexOf(x.id) !== -1;
                        const dataAttributes: Record<string, string> = {
                            selected: isSelected ? 'true' : 'false'
                        };

                        if ('icon' in x) {
                            icon = x.icon;
                            title = x.title;
                            subTitle = x.subTitle;
                        } else {
                            icon = x.medium.icon;
                            title = getMediumTitle(x.medium, locale);
                            dataAttributes['medium-name'] = x.medium.name;
                            if (x.basePrice > 0) {
                                dataAttributes['medium-price'] = x.basePrice.toString();
                            }
                            info = (
                                <>
                                    {getMediumExamples(x.medium, locale)}
                                    <div className='flex flex-col gap-y-2 text-sm 2xl:text-base'>
                                        <Feature text={t('included-characters', { included: x.basePriceCharacters })} />
                                        <Feature
                                            text={t('price-additional-characters', {
                                                price: (x.basePrice / x.basePriceCharacters).toFixed(2),
                                                currency: x.currency
                                            })}
                                        />
                                        {x.timeframeUnit
                                            ? (
                                                <Feature
                                                    text={t('restricted-usage', {
                                                        value: x.timeframeValue || 1,
                                                        unit: x.timeframeUnit
                                                    })}
                                                />
                                            )
                                            : <Feature text={t('unrestricted-usage')} />}
                                    </div>
                                </>
                            );
                            subTitle = t('price-from', { currency: x.currency, basePrice: x.basePrice.toFixed(2) });
                        }

                        return (
                            <Tile
                                key={x.id}
                                icon={icon}
                                info={info}
                                title={title}
                                subTitle={subTitle}
                                className='shrink-0'
                                isSelected={isSelected}
                                onClick={() => onSelectedMediumChange(x.id)}
                                sizeClassNames={sizeClassNames}
                                dataAttributes={dataAttributes}
                            >
                                {children}
                            </Tile>
                        );
                    })}
                </div>
            </ScrollableRow>
            <div className='flex lg:hidden flex-col rounded-lg overflow-hidden gap-0.5'>
                {availableMediums.map(x => {
                    let icon: string;
                    let title: ReactNode;
                    let subTitle: ReactNode;
                    let info: ReactNode | undefined;
                    const isSelected = selectedMediums.indexOf(x.id) !== -1;
                    const dataAttributes: Record<string, string> = {
                        selected: isSelected ? 'true' : 'false'
                    };

                    if ('icon' in x) {
                        icon = x.icon;
                        title = x.title;
                        subTitle = x.subTitle;
                    } else {
                        icon = x.medium.icon;
                        title = getMediumTitle(x.medium, locale);
                        dataAttributes['medium-name'] = x.medium.name;
                        if (x.basePrice > 0) {
                            dataAttributes['medium-price'] = x.basePrice.toString();
                        }
                        info = (
                            <>
                                {getMediumExamples(x.medium, locale)}
                                <div className='flex flex-col gap-y-2 text-sm 2xl:text-base'>
                                    <Feature text={t('included-characters', { included: x.basePriceCharacters })} />
                                    <Feature
                                        text={t('price-additional-characters', {
                                            price: (x.basePrice / x.basePriceCharacters).toFixed(2),
                                            currency: x.currency
                                        })}
                                    />
                                    {x.timeframeUnit
                                        ? (
                                            <Feature
                                                text={t('restricted-usage', {
                                                    value: x.timeframeValue || 1,
                                                    unit: x.timeframeUnit
                                                })}
                                            />
                                        )
                                        : <Feature text={t('unrestricted-usage')} />}
                                </div>
                            </>
                        );
                        subTitle = t('price-from', { currency: x.currency, basePrice: x.basePrice.toFixed(2) });
                    }

                    return (
                        <MobileMedium
                            key={x.id}
                            icon={icon}
                            info={info}
                            title={title}
                            subTitle={subTitle}
                            isSelected={isSelected}
                            onClick={() => onSelectedMediumChange(x.id)}
                            dataAttributes={dataAttributes}
                        />
                    );
                })}
            </div>
        </div>
    );
}

interface MobileMediumProps {
    icon: string;
    info?: ReactNode | string;
    title: ReactNode | string;
    subTitle: ReactNode | string;
    isSelected: boolean;
    onClick: () => void;
    className?: string;
}

export function MobileMedium({
    icon,
    info,
    title,
    subTitle,
    isSelected,
    onClick,
    dataAttributes,
}: PropsWithDataAttributes<MobileMediumProps>) {
    return (
        <div
            className={clsx(
                'flex flex-row gap-x-2 p-2 items-center',
                isSelected ? 'bg-crispybluedark-600 text-white' : 'bg-white text-crispybluedark-600'
            )}
            onClick={onClick}
            {...renderDataAttributes(dataAttributes)}
        >
            <Icon icon={icon} className='shrink-0' />
            <div className='mr-auto'>{title}</div>
            <p className='text-xs'>{subTitle}</p>
            {info && (
                <div onClick={(e) => e.stopPropagation()}>
                    <FlowbiteTooltip
                        content={<div>{info}</div>}
                        arrow={false}
                        placement='left'
                        trigger='click'
                        className='max-w-60 xs:max-w-none'
                    >
                        <InformationCircleIcon
                            className={clsx(
                                'cursor-help shrink-0 w-6 h-6',
                                isSelected ? 'text-white' : 'text-crispybluedark-600'
                            )}
                        />
                    </FlowbiteTooltip>
                </div>
            )}
        </div>
    );
}

interface OrderCardProps {
    allowedMediums: {
        id: string;
        medium: { name: string; titleEn: string | null; titleDe: string | null };
        basePrice: number;
        basePriceCharacters: number;
    }[];
    onCanOrderChange: (accepted: boolean) => void;
    currency: string;
}

export function OrderCard({ allowedMediums, onCanOrderChange, currency, children }: PropsWithChildren<OrderCardProps>) {
    const locale = useLocale();
    const { t, rawT } = useEditableTranslation('checkout');
    const { recordings } = useRecordingContext();
    const translatePath = useTranslatedPath(locale);
    const [ acceptTerms, setAcceptTerms ] = useState(false);

    const onAcceptTermsChange = (checked: boolean) => {
        setAcceptTerms(checked);
        onCanOrderChange(checked && recordings.some(r => r.takes.some(t => t.isChecked)));
    };

    const [ appliedCoupon, setAppliedCoupon ] = useState<CouponInfo | null>(null);

    const originalPrice = useMemo(() => {
        if (recordings.length > 0) {
            return allowedMediums.reduce(
                (total, allowedUse) =>
                    total + recordings.reduce((acc, x) => acc + calculatePriceForRecording(x, allowedUse).price, 0),
                0
            );
        }

        return allowedMediums.reduce((acc, allowedUse) => acc + allowedUse.basePrice, 0);
    }, [ allowedMediums, recordings ]);
    const discountedPrice = useMemo(() => {
        if (appliedCoupon) {
            if (appliedCoupon.type === 'percentage') {
                return originalPrice * (1 - appliedCoupon.discount / 100);
            }
        }

        return originalPrice;
    }, [ appliedCoupon, originalPrice ]);

    useEffect(() => {
        trackCheckoutTotalSum(originalPrice, discountedPrice, appliedCoupon?.code);
    }, [ originalPrice, discountedPrice, appliedCoupon ]);

    useEffect(() => {
        onCanOrderChange(acceptTerms && recordings.some(r => r.takes.some(t => t.isChecked)));
    }, [ acceptTerms, recordings, onCanOrderChange ]);

    return (
        <>
            <div className={clsx(recordings.length > 0 && 'text-lg lg:block', 'hidden 2xl:hidden')}>&nbsp;</div>
            <div
                className={clsx(
                    'lg:bg-crispybluedark-700 lg:rounded-2xl lg:px-6 lg:py-6 text-white flex flex-col lg:gap-y-6 lg:sticky',
                    'lg:top-4 lg:border lg:border-black lg:self-start lg:w-full lg:max-w-90',
                    'gap-4'
                )}
            >
                <div className='rounded-lg lg:rounded-none bg-crispybluedark-600 lg:bg-transparent p-4 lg:p-0 gap-y-2 flex flex-col lg:gap-y-0 lg:block'>
                    {appliedCoupon && (
                        <div className='relative inline-block w-fit line-through'>
                            <span>{currency} {originalPrice.toFixed(2)}</span>
                            {
                                /* <svg className='absolute inset-0 w-full h-full pointer-events-none' preserveAspectRatio='none'>
                            <line x1='0' y1='100%' x2='100%' y2='0' stroke='red' strokeWidth='2' />
                        </svg> */
                            }
                        </div>
                    )}
                    <h1 className={clsx('flex items-baseline font-bold', appliedCoupon && 'text-green-500')}>
                        <span className='mr-4 mb-1 text-3xl'>{currency}</span>{' '}
                        <span className='text-3xl sm:text-5xl md:text-7xl'>{discountedPrice.toFixed(2)}</span>
                    </h1>
                </div>
                <div className='text-base italic gap-y-3 hidden lg:flex flex-col'>
                    <Feature text={t('voice-owner-approved')} />
                    <Feature
                        text={
                            <>
                                {t('allowed-medium', { count: allowedMediums.length })}:{' '}
                                {allowedMediums.map((x, i) => (
                                    <Fragment key={x.id}>{i > 0 && ', '} {getMediumTitle(x.medium, locale)}</Fragment>
                                ))}
                            </>
                        }
                    />
                    <Feature text={<>{t('format')}: {t('common:formats.mp3')}, 128 kBit/s</>} />
                    <Feature text={t('including-release-document')} />
                    <Feature text={t('download-after-payment')} />
                </div>
                <input type='hidden' name='currency' value={currency} />
                {allowedMediums.map((x, i) => (
                    <input key={x.id} type='hidden' name={`allowedMediums[${i}]`} value={x.id} />
                ))}
                {recordings.map((recording, i) =>
                    recording.takes.filter(take =>
                        take.isChecked
                    ).map((take, j) => (
                        <Fragment key={take.takeId}>
                            {renderHiddenFieldsFromDiscriminatedUnion({
                                discriminator: 'voiceType',
                                namePrefix: `recordings[${i}][takes][${j}]`,
                                schema: minimalTakeSchema,
                                value: take
                            })}
                        </Fragment>
                    ))
                )}
                <div className='rounded-lg lg:rounded-none bg-crispybluedark-600 lg:bg-transparent p-4 lg:p-0 gap-y-2 lg:gap-y-4 flex flex-col lg:mt-2'>
                    <CouponEntry onCouponApplied={setAppliedCoupon} />
                    <Checkbox
                        label={
                            <span>
                                <Trans t={rawT} i18nKey='accept-terms'>
                                    <a href={translatePath('/gtc')} target='_blank' className='underline'></a>
                                    <ArrowTopRightOnSquareIcon className='ml-1 inline h-5 w-5' />
                                </Trans>
                            </span>
                        }
                        isChecked={acceptTerms}
                        onChange={onAcceptTermsChange}
                        name='acceptTerms'
                    />
                    {children}
                </div>
            </div>
        </>
    );
}

interface Opts {
    hasConsent: boolean;
    isLoggedIn: boolean;
    anonymousId: string | null;
    limits: {
        generations?: {
            max: number;
            used: number;
            remaining: number;
        };
        maxCharacters?: number;
    } | null;
    defaultAudioControls: VoiceData;
    requestDataRefresh: () => void;
}

function createPlaceholderRecordings(t: TranslateFunc): RecordingRow[] {
    return [ { isPlaceholder: true, takes: [ { isPlaceholder: true, text: t('no-recordings-yet') } ] } ];
}

function AudioGenerationContainer(opts: Opts) {
    const { t } = useEditableTranslation('audio-generation');
    const { recordings } = useRecordingContext();
    const { rawT: tCheckout } = useEditableTranslation('checkout');
    const placeholderRecordings = useMemo(() => createPlaceholderRecordings(tCheckout), [ tCheckout ]);
    return (
        <AudioGeneration {...opts}>
            {({
                renderTextArea,
                renderForm,
                progressBar,
                characterCounter,
                characterLimitReached,
                remainingGenerations,
                limitOverlay,
                consentOverlay,
                voiceSettings,
                autoplayBlocked,
                error,
                blockedAndUnchanged,
                isPlayingAudio,
                onGenerate,
                onStop,
                canGenerate,
            }) => (
                <div className='grid grid-cols-1 grid-rows-1 2xl:overflow-hidden 2xl:rounded-2xl'>
                    <div className='col-start-1 row-start-1'>
                        <div className='grid grid-cols-1 grid-rows-1'>
                            {limitOverlay.has || consentOverlay.has
                                ? (
                                    <div className='col-start-1 row-start-1 z-10'>
                                        {limitOverlay.render()}
                                        {consentOverlay.render()}
                                    </div>
                                )
                                : null}
                            <div className='col-start-1 row-start-1 w-full relative z-0'>
                                {renderForm(
                                    <div className='relative 2xl:bg-white bg-gray-200 rounded-lg 2xl:rounded-none py-2 2xl:pt-6 2xl:pb-4 2xl:pr-4 px-2 gap-4 flex flex-col items-end'>
                                        {renderTextArea({
                                            rows: 5,
                                            className: hasError =>
                                                clsx(
                                                    '2xl:col-span-3 w-full py-4 2xl:py-0 px-4 resize-none bg-white text-black text-2xl placeholder:2xl 2xl:text-3xl 2xl:placeholder:text-3xl focus:ring-0 focus:outline-none rounded-lg 2xl:rounded-none placeholder:text-center placeholder:leading-40',
                                                    hasError && 'border-2'
                                                )
                                        })}
                                        <div className='absolute text-zinc-600 text-xs 2xl:text-sm w-max top-3 right-4 2xl:top-1 2xl:right-4'>
                                            <Tooltip
                                                trigger={
                                                    <div className='flex items-center cursor-help'>
                                                        <Icon icon='heroicons--information-circle' className='mr-2' />
                                                        {t('available-languages')}
                                                    </div>
                                                }
                                            >
                                                <div className='w-60 2xl:w-120'>{t('supported-languages')}</div>
                                            </Tooltip>
                                        </div>
                                        {error && (
                                            <Alert color='failure' className='w-full'>
                                                <div className='grid grid-cols-[auto,1fr] grid-rows-2'>
                                                    <Icon
                                                        icon='heroicons--exclamation-triangle'
                                                        className='shrink-0 mr-2 place-self-center'
                                                    />
                                                    <p className='font-semibold col-start-2 row-start-1'>
                                                        {t('audio-generation:error-title')}
                                                    </p>
                                                    <div className='col-start-2 row-start-2'>
                                                        <p>{error}</p>
                                                        {blockedAndUnchanged && (
                                                            <p className='mt-1 text-red-700'>
                                                                {t(
                                                                    'audio-generation:editToContinue',
                                                                    'Please edit your text to try again.'
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}
                                        <div className='w-full flex flex-col md:flex-row justify-between'>
                                            <div className='col-span-3 text-xs sm:text-sm font-normal flex flex-row items-center text-black'>
                                                <Icon
                                                    icon='heroicons--information-circle'
                                                    className='mr-2 shrink-0 hidden 2xl:block'
                                                />
                                                <div>{t('audio-generation:copy-protection-info')}</div>
                                            </div>
                                            <div className='flex items-center gap-x-4 shrink-0 self-center xs:self-end md:self-auto mt-4 md:mt-0'>
                                                <Button
                                                    className='rounded-lg'
                                                    color='yellow'
                                                    type='button'
                                                    onClick={isPlayingAudio ? onStop : onGenerate}
                                                    disabled={isPlayingAudio ? false : !canGenerate}
                                                >
                                                    <Icon
                                                        icon={isPlayingAudio ? 'fa6-solid--stop' : 'fa--magic'}
                                                        className='mr-2'
                                                    />
                                                    {isPlayingAudio
                                                        ? t('buttons.stop')
                                                        : t('audio-generation:buttons.generate-audio')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className='text-sm flex flex-col gap-y-4 mt-4'>
                                    {characterLimitReached.render()}
                                    <div className='grid-cols-3 items-center hidden md:grid'>
                                        {voiceSettings.render()}
                                        <div className='justify-self-center'>{characterCounter.render()}</div>
                                        <div className='justify-self-center'>{remainingGenerations.render()}</div>
                                    </div>

                                    <div className='justify-center flex md:hidden'>{voiceSettings.render()}</div>
                                    <div className='flex justify-between flex-col xs:flex-row md:hidden text-center'>
                                        {characterCounter.render()}
                                        {remainingGenerations.render()}
                                    </div>
                                </div>
                                {progressBar.has && (
                                    <div className='absolute -left-4 -right-4 -bottom-4 -top-4 pt-8 backdrop-blur-sm rounded-t-2xl z-10'>
                                        {progressBar.render()}
                                    </div>
                                )}
                                {autoplayBlocked.has && <div className='mt-4'>{autoplayBlocked.render()}</div>}
                            </div>
                        </div>
                        {recordings.length > 0 && (
                            <div className='2xl:overflow-hidden lg:hidden 2xl:block mt-4'>
                                <Recordings recordings={recordings} />
                            </div>
                        )}
                        {recordings.length === 0 && (
                            <div className='2xl:overflow-hidden hidden 2xl:block mt-4'>
                                <Recordings recordings={placeholderRecordings} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </AudioGeneration>
    );
}
interface RecordingsProps {
    recordings: RecordingRow[];
}

export function Recordings({ recordings }: RecordingsProps) {
    return (
        <div className='gap-4 2xl:gap-0 flex flex-col'>
            {recordings.map((x, index) => (
                <Recording
                    recording={x}
                    key={'isPlaceholder' in x ? 'placeholder' : x.takes[0]!.takeId}
                    index={index}
                />
            ))}
        </div>
    );
}

interface RecordingProps {
    recording: RecordingRow;
    index: number;
}

function Recording({ recording, index }: RecordingProps) {
    const { t } = useEditableTranslation('checkout');
    const { setCheckState, deleteTake } = useRecordingContext();
    const setIsChecked = useCallback(
        (textId: string, isChecked: boolean) => setCheckState(textId, isChecked),
        [ setCheckState ]
    );
    const isPlaceholder = 'isPlaceholder' in recording;

    return (
        <div
            className='2xl:bg-white 2xl:border 2xl:border-gray-200 2xl:shadow-sm gap-4 2xl:gap-0 flex flex-col'
            data-recording-index={index}
        >
            <div
                className={clsx(
                    '2xl:px-4 2xl:py-3 2xl:border-b 2xl:border-gray-200 2xl:bg-gray-50 2xl:block',
                    isPlaceholder && 'hidden'
                )}
            >
                <h2 className='text-lg font-semibold 2xl:text-gray-900'>{isPlaceholder ? '\u00a0' : recording.name}</h2>
            </div>
            <div className='2xl:p-4'>
                <div className='overflow-x-auto hidden 2xl:block'>
                    <table className='min-w-full divide-y divide-gray-200'>
                        <thead className='bg-gray-50'>
                            <tr>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.version')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.text')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.actions')}
                                </th>
                                <th className='px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wider'>
                                    {t('columns.buy')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                            {recording.takes.map((take, index) => {
                                const isTakePlaceholder = 'isPlaceholder' in take;
                                return (
                                    <TakeRow
                                        key={isTakePlaceholder ? 'placeholder' : take.takeId}
                                        take={take}
                                        index={index}
                                        setIsChecked={(isChecked) =>
                                            isTakePlaceholder ? undefined : setIsChecked(take.takeId, isChecked)}
                                        onDelete={() => isTakePlaceholder ? undefined : deleteTake(take.takeId)}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className='2xl:hidden gap-4 flex flex-col'>
                    {recording.takes.map((take, index) => {
                        const isTakePlaceholder = 'isPlaceholder' in take;
                        return (
                            <MobileTakeRow
                                key={isTakePlaceholder ? 'placeholder' : take.takeId}
                                take={take}
                                index={index}
                                setIsChecked={(isChecked) =>
                                    isTakePlaceholder ? undefined : setIsChecked(take.takeId, isChecked)}
                                onDelete={() => isTakePlaceholder ? undefined : deleteTake(take.takeId)}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

interface TakeRowProps {
    take: TakeRowModel;
    setIsChecked: (isChecked: boolean) => void;
    onDelete: () => void;
    index: number;
}

function TableColumn({ children, className }: PropsWithChildren<{ className?: string }>) {
    return <td className={clsx('p-4 text-gray-900', className)}>{children}</td>;
}

export function TakeRow({ take, setIsChecked, onDelete, index }: TakeRowProps) {
    const { t, rawT } = useEditableTranslation('checkout');
    const isPlaceholder = 'isPlaceholder' in take;
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const hasAudio = useMemo(() => isPlaceholder ? false : hasLoadedAudio(take.audio), [ isPlaceholder, take ]);
    const isRestored = useMemo(() => isPlaceholder ? false : isRestoredTake(take), [ isPlaceholder, take ]);

    return (
        <tr className='text-xl relative' data-take-row-index={index}>
            <TableColumn className='w-32'>{isPlaceholder ? '' : take.name}</TableColumn>
            <TableColumn>
                <span data-take-text className='content'>{take.text}</span>
            </TableColumn>
            <TableColumn className='w-16'>
                <div className='flex justify-self-center gap-x-4'>
                    <PlayButton
                        audio={isPlaceholder ? '' : take.audio}
                        stopTitle={rawT('stop')}
                        playTitle={rawT('play')}
                        takeId={isPlaceholder ? '' : take.takeId}
                        buttonClassName='justify-self-center'
                    />
                    <IconButton
                        icon='heroicons--trash'
                        className={clsx(
                            'justify-self-center',
                            (hasAudio || isRestored)
                                ? 'cursor-pointer text-red-600'
                                : 'cursor-not-allowed text-slate-500'
                        )}
                        onClick={() => setShowDeleteConfirm(true)}
                    />
                </div>
            </TableColumn>
            <TableColumn className='w-16'>
                <Checkbox
                    isChecked={isPlaceholder ? false : take.isChecked}
                    onChange={setIsChecked}
                    label=''
                    uncheckedOnlyBorder={true}
                    className={clsx(
                        'text-xl items-center flex justify-self-center',
                        (hasAudio || isRestored) ? 'text-crispybluedark-700' : 'cursor-not-allowed text-slate-500'
                    )}
                />
            </TableColumn>
            <td className='w-0'>
                {showDeleteConfirm && (
                    <div className='absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center z-10 text-base px-4'>
                        <div className='flex flex-row justify-end w-full'>
                            <div className='flex flex-row gap-4 items-center '>
                                <div>{t('confirmDeleteTake')}</div>
                                <Button
                                    className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
                                    onClick={() => {
                                        onDelete();
                                        setShowDeleteConfirm(false);
                                    }}
                                >
                                    {t('delete')}
                                </Button>
                                <Button
                                    className='px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400'
                                    onClick={() => setShowDeleteConfirm(false)}
                                >
                                    {t('cancel')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
}

function MediumSizeRecordings() {
    const { recordings } = useRecordingContext();
    const { rawT } = useEditableTranslation('checkout');
    const placeholderRecordings = useMemo(() => createPlaceholderRecordings(rawT), [ rawT ]);
    return (
        <div className='hidden lg:flex lg:flex-col lg:justify-top 2xl:hidden grow'>
            <Recordings recordings={recordings.length ? recordings : placeholderRecordings} />
        </div>
    );
}

interface MobileTakeRowProps {
    take: TakeRowModel;
    setIsChecked: (isChecked: boolean) => void;
    onDelete: () => void;
    index: number;
}
function MobileTakeRow({ take, setIsChecked, onDelete, index }: MobileTakeRowProps) {
    const { t, rawT } = useEditableTranslation('checkout');
    const isPlaceholder = 'isPlaceholder' in take;
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const hasAudio = useMemo(() => isPlaceholder ? false : hasLoadedAudio(take.audio), [ isPlaceholder, take ]);
    const isRestored = useMemo(() => isPlaceholder ? false : isRestoredTake(take), [ isPlaceholder, take ]);

    return (
        <div
            className={clsx(
                'rounded-lg p-4 gap-4 flex flex-col relative text-white bg-crispybluedark-700',
                !isPlaceholder && take.isChecked ? 'border-yellow-300 border-2' : 'border-black border-1'
            )}
            data-take-index={index}
        >
            {showDeleteConfirm && (
                <div className='absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center z-10 text-base px-4 m-1'>
                    <div className='flex flex-row justify-end w-full'>
                        <div className='flex flex-row gap-4 items-center '>
                            <div>{t('confirmDeleteTake')}</div>
                            <Button
                                className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
                                onClick={() => {
                                    onDelete();
                                    setShowDeleteConfirm(false);
                                }}
                            >
                                {t('delete')}
                            </Button>
                            <Button
                                className='px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400'
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                {t('cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div className='flex flex-row gap-4'>
                <PlayButton
                    audio={isPlaceholder ? '' : take.audio}
                    stopTitle={rawT('stop')}
                    playTitle={rawT('play')}
                    takeId={isPlaceholder ? '' : take.takeId}
                    buttonClassName='text-yellow-300'
                />
                {isPlaceholder ? '' : take.name}
                <div className='grow' />
                <Checkbox
                    isChecked={isPlaceholder ? false : take.isChecked}
                    onChange={setIsChecked}
                    uncheckedOnlyBorder={true}
                    labelPosition='left'
                    label='kaufen'
                    className={clsx(
                        (hasAudio || isRestored) ? 'text-white' : 'cursor-not-allowed text-slate-500'
                    )}
                />
                <IconButton
                    icon='heroicons--trash-solid'
                    className={clsx(
                        'justify-self-center',
                        (hasAudio || isRestored) ? 'cursor-pointer text-red-400' : 'cursor-not-allowed text-slate-500'
                    )}
                    onClick={() => setShowDeleteConfirm(true)}
                />
            </div>
            <div>
                <div className='text-xs'>Text:</div>
                <span data-take-text className='content'>{take.text}</span>
            </div>
        </div>
    );
}
